import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, Building2, Globe, Hash, Mail, MapPin, Phone, Check, Loader2 } from "lucide-react";
import { OnboardingData } from "../index";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";

interface BusinessDetailsProps {
  onNext: () => void;
  onPrevious: () => void;
  onboardingData: OnboardingData;
  updateOnboardingData: (data: Partial<OnboardingData>) => void;
}

const phoneRegex = /^(\+\d{1,3})?\s*\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;

const businessDetailsSchema = z.object({
  businessName: z.string().min(2, "Business name is required"),
  businessType: z.string().min(1, "Please select a business type"),
  industry: z.string().min(1, "Please select an industry"),
  ein: z.string().optional(),
  phoneNumber: z.string()
    .min(10, "Phone number must be at least 10 digits")
    .regex(phoneRegex, "Please enter a valid phone number format")
    .refine(val => val.replace(/\D/g, '').length >= 10, {
      message: "Phone number must have at least 10 digits",
    }),
  website: z.string().optional(),
  address: z.object({
    street: z.string().min(5, "Street address is required"),
    city: z.string().optional(),
    state: z.string().optional(),
    zip: z.string().optional(),
    country: z.string().default("United States"),
  }),
});

type BusinessDetailsFormValues = z.infer<typeof businessDetailsSchema>;

// Mock address verification API
const verifyAddress = async (address: string): Promise<{ 
  isValid: boolean;
  suggestions?: string[];
  formattedAddress?: string;
}> => {
  return new Promise((resolve) => {
    // Simulate API call delay
    setTimeout(() => {
      // Basic validation - more than 5 characters and contains a number
      const hasNumber = /\d/.test(address);
      const isLongEnough = address.length > 5;
      const isValid = hasNumber && isLongEnough;
      
      if (isValid) {
        const formattedAddress = address
          .replace(/(\d+)/, (match) => match + " ")
          .replace(/\s+/g, " ")
          .trim();
        
        resolve({
          isValid: true,
          formattedAddress: formattedAddress.charAt(0).toUpperCase() + formattedAddress.slice(1)
        });
      } else {
        resolve({
          isValid: false,
          suggestions: [
            "Make sure to include a street number",
            "Enter a complete street address",
            "Address should be at least 5 characters"
          ]
        });
      }
    }, 500);
  });
};

export default function BusinessDetails({
  onNext,
  onPrevious,
  onboardingData,
  updateOnboardingData,
}: BusinessDetailsProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifyingAddress, setIsVerifyingAddress] = useState(false);
  const [addressVerified, setAddressVerified] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState<string[]>([]);
  
  const form = useForm<BusinessDetailsFormValues>({
    resolver: zodResolver(businessDetailsSchema),
    defaultValues: {
      businessName: onboardingData.businessName || "",
      businessType: onboardingData.businessType || "",
      industry: onboardingData.industry || "",
      ein: onboardingData.ein || "",
      phoneNumber: onboardingData.phoneNumber || "",
      website: onboardingData.website || "",
      address: {
        street: onboardingData.address?.street || "",
        city: onboardingData.address?.city || "",
        state: onboardingData.address?.state || "",
        zip: onboardingData.address?.zip || "",
        country: onboardingData.address?.country || "United States",
      },
    },
  });
  
  const onSubmit = async (values: BusinessDetailsFormValues) => {
    try {
      setIsLoading(true);
      
      // Store data in onboarding context
      updateOnboardingData({
        businessName: values.businessName,
        businessType: values.businessType,
        industry: values.industry,
        ein: values.ein,
        phoneNumber: values.phoneNumber,
        website: values.website,
        address: values.address,
      });
      
      // Fake API delay for demo purposes
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Success notification
      toast({
        title: "Business details saved",
        description: "Your business information has been saved successfully.",
      });
      
      // Continue to next step
      onNext();
    } catch (error) {
      toast({
        title: "Error",
        description: "There was a problem saving your business details. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="max-w-2xl mx-auto">
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold">Business Information</h2>
          <p className="mt-2 text-neutral-600">
            Tell us about your business to customize your ZentroBooks experience
          </p>
        </div>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="businessName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Business Name</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Building2 className="absolute left-3 top-3 h-4 w-4 text-neutral-400" />
                            <Input
                              placeholder="Enter your business name"
                              className="pl-9"
                              {...field}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="businessType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Business Type</FormLabel>
                          <Select 
                            onValueChange={field.onChange} 
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select business type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="soleProprietorship">Sole Proprietorship</SelectItem>
                              <SelectItem value="llc">LLC</SelectItem>
                              <SelectItem value="partnership">Partnership</SelectItem>
                              <SelectItem value="corporation">Corporation</SelectItem>
                              <SelectItem value="sCorporation">S Corporation</SelectItem>
                              <SelectItem value="nonprofit">Nonprofit</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="industry"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Industry</FormLabel>
                          <Select 
                            onValueChange={field.onChange} 
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select industry" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="retail">Retail</SelectItem>
                              <SelectItem value="ecommerce">E-commerce</SelectItem>
                              <SelectItem value="services">Professional Services</SelectItem>
                              <SelectItem value="construction">Construction</SelectItem>
                              <SelectItem value="manufacturing">Manufacturing</SelectItem>
                              <SelectItem value="hospitality">Hospitality</SelectItem>
                              <SelectItem value="technology">Technology</SelectItem>
                              <SelectItem value="healthcare">Healthcare</SelectItem>
                              <SelectItem value="education">Education</SelectItem>
                              <SelectItem value="finance">Finance</SelectItem>
                              <SelectItem value="realEstate">Real Estate</SelectItem>
                              <SelectItem value="creative">Creative & Media</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="ein"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          EIN (Employer Identification Number)
                          <span className="ml-1 text-neutral-400 text-sm">(Optional)</span>
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Hash className="absolute left-3 top-3 h-4 w-4 text-neutral-400" />
                            <Input
                              placeholder="XX-XXXXXXX"
                              className="pl-9"
                              {...field}
                            />
                          </div>
                        </FormControl>
                        <FormDescription>
                          Your EIN is used for tax purposes. If you don't have one yet, you can add it later.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-medium mb-4">Contact Information</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="phoneNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Phone Number
                            <span className="ml-1 text-red-500">*</span>
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Phone className="absolute left-3 top-3 h-4 w-4 text-neutral-400" />
                              <Input
                                placeholder="(XXX) XXX-XXXX"
                                className="pl-9"
                                {...field}
                                onChange={(e) => {
                                  // Format the phone number as user types
                                  let value = e.target.value.replace(/\D/g, '');
                                  if (value.length > 0) {
                                    if (value.length <= 3) {
                                      value = `(${value}`;
                                    } else if (value.length <= 6) {
                                      value = `(${value.slice(0, 3)}) ${value.slice(3)}`;
                                    } else {
                                      value = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`;
                                    }
                                  }
                                  field.onChange(value);
                                }}
                              />
                            </div>
                          </FormControl>
                          <FormDescription className="text-xs">
                            Enter a valid US phone number, e.g., (123) 456-7890
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="website"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Website
                            <span className="ml-1 text-neutral-400 text-sm">(Optional)</span>
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Globe className="absolute left-3 top-3 h-4 w-4 text-neutral-400" />
                              <Input
                                placeholder="https://yourbusiness.com"
                                className="pl-9"
                                {...field}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-medium mb-4">Business Address</h3>
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="address.street"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Street Address
                          <span className="ml-1 text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <MapPin className="absolute left-3 top-3 h-4 w-4 text-neutral-400" />
                            <Input
                              placeholder="123 Business St."
                              className={`pl-9 ${addressVerified ? 'border-green-500' : ''}`}
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                
                                // Reset verification status when typing
                                if (addressVerified) {
                                  setAddressVerified(false);
                                }
                                
                                // Clear suggestions when field is emptied
                                if (!e.target.value) {
                                  setAddressSuggestions([]);
                                }
                              }}
                              onBlur={async (e) => {
                                field.onBlur();
                                if (e.target.value && e.target.value.length > 3) {
                                  setIsVerifyingAddress(true);
                                  setAddressSuggestions([]);
                                  
                                  try {
                                    const result = await verifyAddress(e.target.value);
                                    
                                    if (result.isValid) {
                                      setAddressVerified(true);
                                      if (result.formattedAddress) {
                                        field.onChange(result.formattedAddress);
                                      }
                                      toast({
                                        title: "Address verified",
                                        description: "Your business address has been validated",
                                      });
                                    } else {
                                      setAddressVerified(false);
                                      if (result.suggestions) {
                                        setAddressSuggestions(result.suggestions);
                                      }
                                    }
                                  } catch (error) {
                                    toast({
                                      title: "Verification failed",
                                      description: "Could not verify address at this time",
                                      variant: "destructive",
                                    });
                                  } finally {
                                    setIsVerifyingAddress(false);
                                  }
                                }
                              }}
                            />
                            {isVerifyingAddress && (
                              <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-neutral-500" />
                            )}
                            {addressVerified && (
                              <Check className="absolute right-3 top-3 h-4 w-4 text-green-500" />
                            )}
                          </div>
                        </FormControl>
                        {addressSuggestions.length > 0 && (
                          <div className="mt-2 space-y-1">
                            <p className="text-sm font-medium text-amber-500">Please check your address:</p>
                            <ul className="text-xs text-neutral-600 space-y-1 ml-5 list-disc">
                              {addressSuggestions.map((suggestion, i) => (
                                <li key={i}>{suggestion}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {addressVerified && (
                          <Badge variant="outline" className="mt-2 text-xs font-normal text-green-600 border-green-200 bg-green-50">
                            <Check className="mr-1 h-3 w-3" /> Address verified
                          </Badge>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="address.city"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            City
                            <span className="ml-1 text-neutral-400 text-sm">(Optional)</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="City"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="address.state"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            State
                            <span className="ml-1 text-neutral-400 text-sm">(Optional)</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="State"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="address.zip"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            ZIP Code
                            <span className="ml-1 text-neutral-400 text-sm">(Optional)</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="ZIP"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="address.country"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Country</FormLabel>
                        <Select 
                          onValueChange={field.onChange} 
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select country" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="United States">United States</SelectItem>
                            <SelectItem value="Canada">Canada</SelectItem>
                            <SelectItem value="Mexico">Mexico</SelectItem>
                            <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                            <SelectItem value="Australia">Australia</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>
            
            <div className="flex justify-between pt-4">
              <Button type="button" variant="outline" onClick={onPrevious}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  "Saving..."
                ) : (
                  <>
                    Continue
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}